import { useState, useEffect, useCallback, useRef } from "react";
import { useDialogFocus } from "../hooks/useDialogFocus";
import { TeamTypeahead, type TeamOption } from "./TeamTypeahead";
import { ModalCloseButton } from "./ModalCloseButton";
import "./AddTeamModal.css";

export interface AddTeamResult {
  team: TeamOption;
  jerseyNumber: number | null;
}

interface AddTeamModalProps {
  isOpen: boolean;
  prospectName: string;
  onSave: (result: AddTeamResult) => void;
  onClose: () => void;
}

export function AddTeamModal({
  isOpen,
  prospectName,
  onSave,
  onClose,
}: AddTeamModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: Boolean(isOpen), dialogRef, handleEscape: false });

  const [selectedTeam, setSelectedTeam] = useState<TeamOption | null>(null);
  const [jerseyText, setJerseyText] = useState("");

  // Reset state when modal opens
  useEffect(() => {

    if (isOpen) {
      setSelectedTeam(null);
      setJerseyText("");
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [isOpen, onClose]);

  // Parse jersey number from text
  const parseJerseyNumber = (): number | null => {
    const trimmed = jerseyText.trim();
    if (trimmed === "") return null;
    const num = parseInt(trimmed, 10);
    if (!isNaN(num) && num >= 0 && num <= 99) {
      return num;
    }
    return null;
  };

  // Handle jersey input change
  const handleJerseyChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const numericText = e.target.value.replace(/[^0-9]/g, "");
      if (numericText.length <= 2) {
        setJerseyText(numericText);
      }
    },
    []
  );

  // Handle save
  const handleSave = useCallback(() => {
    if (selectedTeam && selectedTeam.name) {
      onSave({
        team: selectedTeam,
        jerseyNumber: parseJerseyNumber(),
      });
    }
  }, [selectedTeam, jerseyText, onSave]);

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const canSave = selectedTeam && selectedTeam.name;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="add-team-modal__overlay" onClick={handleOverlayClick}>
      <div ref={dialogRef} tabIndex={-1}
        className="add-team-modal__content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-team-modal-title"
      >
        {/* Header */}
        <div className="add-team-modal__header">
          <div className="add-team-modal__header-content">
            <h3 id="add-team-modal-title" className="add-team-modal__title">
              Add to Team
            </h3>
            <p className="add-team-modal__subtitle">{prospectName}</p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Body */}
        <div className="add-team-modal__body">
          {/* Team Selection */}
          <label htmlFor="add-team-modal-team-0" className="add-team-modal__label">Team</label>
          <TeamTypeahead inputId="add-team-modal-team-0"
            value={selectedTeam}
            onSelect={setSelectedTeam}
            placeholder="Search teams..."
          />

          {/* Jersey Number */}
          <label htmlFor="add-team-modal-jersey-number-optional-0" className="add-team-modal__label add-team-modal__label--jersey">
            Jersey Number (optional)
          </label>
          <input id="add-team-modal-jersey-number-optional-0"
            type="text"
            inputMode="numeric"
            className="add-team-modal__jersey-input"
            value={jerseyText}
            onChange={handleJerseyChange}
            placeholder="0-99"
            maxLength={2}
          />
        </div>

        {/* Footer */}
        <div className="add-team-modal__footer">
          <button
            className="add-team-modal__save-button"
            onClick={handleSave}
            disabled={!canSave}
          >
            Add to Team
          </button>
        </div>
      </div>
    </div>
  );
}
